import type { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { getFirstLoadedDate, type LoadedDay, resolveLoadedDay } from "./addStaffUtils";

type Args = {
	/** The day the caller opened the panel on. */
	initial: LoadedDay;
	/** Whether this panel may move to another date at all (not for a request's locked shift, hospital or actuals). */
	enabled: boolean;
	/** Keeps the caller in step with the picked date (Staffing Changes' other tabs follow it). */
	onSelectedDateChange: ((date: DateTime) => void) | undefined;
	/** The panel the date moved in, as the `source` of its analytics and error reports (Add Staff, Remove Staff). */
	analyticsSource: string;
};

export type AddStaffDate = LoadedDay & {
	canChangeDate: boolean;
	/** The pickable range — exactly the loaded weeks, so the panel can never target data that isn't there. */
	minDate: DateTime | undefined;
	maxDate: DateTime | undefined;
	changeDate: (date: DateTime) => void;
};

/**
 * The day a Staffing Changes panel acts on. Starts on the caller's day; the header's date picker re-targets the
 * whole tab (Add Staff: cards, list, week strip, costs; Remove Staff: its list) to another loaded day by moving
 * this `{weekIndex, dayNo}`.
 */
export const useAddStaffDate = ({ initial, enabled, onSelectedDateChange, analyticsSource }: Args): AddStaffDate => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { hospitalData, latestDateInWeeksData, getWeekIndexByDate, getDayIndexByDate, showErrorSnackbar } = useHospitalData();
	const [target, setTarget] = useState<LoadedDay>(initial);
	const minDate = useMemo(() => getFirstLoadedDate(hospitalData.weeks), [hospitalData.weeks]);

	const changeDate = useCallback(
		(date: DateTime) => {
			const next = resolveLoadedDay(date, { getWeekIndexByDate, getDayIndexByDate });
			if (!next) {
				// The picker is bounded to the loaded weeks, so this means a week unloaded under an open picker.
				showErrorSnackbar(t("add.staff.date.not.loaded"), { source: analyticsSource, date: date.toISODate() });
				return;
			}
			if (next.weekIndex === target.weekIndex && next.dayNo === target.dayNo) return;
			setTarget(next);
			track("Staffing - change date", {
				source: analyticsSource,
				from_date: hospitalData.weeks[target.weekIndex].weeklyShifts[0].days[target.dayNo].date.toISODate(),
				to_date: date.toISODate(),
				week_index: next.weekIndex,
				day_no: next.dayNo,
			});
			onSelectedDateChange?.(date);
		},
		[getWeekIndexByDate, getDayIndexByDate, showErrorSnackbar, t, target, track, hospitalData.weeks, onSelectedDateChange, analyticsSource],
	);

	return {
		...target,
		canChangeDate: enabled && !!minDate && !!latestDateInWeeksData,
		minDate,
		maxDate: latestDateInWeeksData,
		changeDate,
	};
};
